// eslint-disable-next-line no-restricted-imports

import React from 'react';
import { useLocation } from 'react-router-dom';
import ProductCard from '@99designs/apps/spa/common/Product/ProductCard';
import SecondaryProductCard from '@99designs/apps/spa/common/Product/ProductCard/SecondaryProductCard';
import { ProductFixedPriceProjectFragment } from '@99designs/apps/spa/pages/create/briefs/Brief.generated';
import { useProductsQuery } from '@99designs/apps/spa/pages/create/common/Products.generated';
import { ButtonWrapper } from '@99designs/apps/spa/pages/create/components/Button';
import ContentSetting from '@99designs/apps/spa/pages/create/components/ContentSetting';
import { parseQueryString } from '@99designs/apps/spa/pages/create/util/queryString';
import { PartnerLogo } from '@99designs/brief';
import { Box, Button, PageHeading, SmallPageHeading, Stack } from '@99designs/emu';
import { LoadableElement } from '@99designs/frontend-components/components/LoadableElement';
import { linkTo } from '@99designs/routing';
import { SquareProductLogo } from '@99designs/spa-common';
import { Partner } from '@99designs/spa-common';
import { ViewProductDetails } from '@99designs/spa-common';
import { packageToProductSummary } from '@99designs/spa-common';

export default function CreateSquareOnlineStore() {
    const location = useLocation();
    const { designerId } = parseQueryString(location.search);

    const { error, data, loading } = useProductsQuery({
        variables: {
            input: {
                categories: ['square-online-store'],
                types: ['PRODUCT_TYPE_FIXED_PRICE_PROJECT'],
            },
        },
    });
    if (error) {
        throw error;
    }

    if (loading) {
        return <LoadableElement variations={['fade-in']} isLoading={true} />;
    }

    if (!data) {
        throw new Error('package not found');
    }

    const packagesByCategory = data.products as ProductFixedPriceProjectFragment[];

    const squareOnlineSelfServeGroup = 'square-online-self-serve';

    const squarePkgs = packagesByCategory.filter(
        (p) => !p.key.includes(squareOnlineSelfServeGroup)
    );

    const squareSelfServePkgs = packagesByCategory.filter((p) =>
        p.key.includes(squareOnlineSelfServeGroup)
    );

    return (
        <ContentSetting>
            <Box marginBottom="1.5bu">
                <PartnerLogo partner={Partner.Square} />
            </Box>

            <Stack space="8bu">
                {squarePkgs.length > 0 && (
                    <Stack space="1.5bu">
                        <Box marginBottom="1.5bu">
                            <PageHeading>{__('What best describes your need?')}</PageHeading>
                        </Box>
                        {squarePkgs.map((p) => {
                            const packageForProductSummary = {
                                title: p.title,
                                description: p.description,
                                amountInCents: p.pricing.amount.amountInCents,
                                currency: p.pricing.amount.currency,
                            };

                            return (
                                <ProductCard
                                    key={p.key}
                                    rightColumn={
                                        <Stack space="1bu">
                                            <Box display="flex" justifyContent="center">
                                                <ViewProductDetails
                                                    features={p.details}
                                                    additionalDetails={p.additionalDetails}
                                                    productSummary={packageToProductSummary({
                                                        ...packageForProductSummary,
                                                        logo: SquareProductLogo,
                                                    })}
                                                />
                                            </Box>
                                            <ButtonWrapper data-testid={`package:${p.key}`}>
                                                <Button
                                                    size="large"
                                                    appearance="primary"
                                                    width="fill"
                                                    href={linkTo.briefCreate(p.key, designerId)}
                                                    type="button"
                                                >
                                                    {__('Start brief')}
                                                </Button>
                                            </ButtonWrapper>
                                        </Stack>
                                    }
                                    productSummary={packageToProductSummary(
                                        packageForProductSummary
                                    )}
                                    features={p.details}
                                    hasLeftBorder={false}
                                />
                            );
                        })}
                    </Stack>
                )}

                {squareSelfServePkgs.length > 0 && (
                    <Stack space="1.5bu">
                        <PageHeading>
                            {__('Need more? Try our new Self-Serve Ordering Cards')}
                        </PageHeading>
                        {squareSelfServePkgs.map((p) => {
                            const packageForProductSummary = {
                                title: p.title,
                                description: p.description,
                                amountInCents: p.pricing.amount.amountInCents,
                                currency: p.pricing.amount.currency,
                            };

                            return (
                                <ProductCard
                                    key={p.key}
                                    rightColumn={
                                        <Stack space="1bu">
                                            <Box display="flex" justifyContent="center">
                                                <ViewProductDetails
                                                    features={p.details}
                                                    additionalDetails={p.additionalDetails}
                                                    productSummary={packageToProductSummary({
                                                        ...packageForProductSummary,
                                                        logo: SquareProductLogo,
                                                    })}
                                                    image={p.image}
                                                />
                                            </Box>
                                            <ButtonWrapper>
                                                <Button
                                                    size="large"
                                                    appearance="secondary"
                                                    width="fill"
                                                    href={linkTo.briefCreate(p.key)}
                                                    type="button"
                                                >
                                                    {__('Start brief')}
                                                </Button>
                                            </ButtonWrapper>
                                        </Stack>
                                    }
                                    productSummary={packageToProductSummary(
                                        packageForProductSummary
                                    )}
                                    features={p.details}
                                    hasLeftBorder={false}
                                />
                            );
                        })}
                    </Stack>
                )}

                <Stack space="1.5bu">
                    <Stack space="0.5bu">
                        <SmallPageHeading>
                            {__('Don’t quite see what you’re looking for?')}
                        </SmallPageHeading>
                        <div>
                            {__(
                                'Our experts can provide quotes for simple eCommerce updates to your current Square Online store, starting from just USD $50. Or see below for more custom project options to get your website humming.'
                            )}
                        </div>
                    </Stack>

                    <SecondaryProductCard
                        title={__('Custom Square Online project')}
                        description={__(
                            'Get tailored quotes from up to 5 designers based on your needs. Through a custom project, you can: create a Square Online Store from scratch with an expert to guide you; migrate your website to a Square Online store so it’s all in one place; or redesign your current Square Online store with a fresh lick of paint from professional designers.'
                        )}
                        fromPrice={{
                            amountInCents: 45000,
                            currency: 'USD',
                        }}
                        cta={
                            <Box marginTop="1bu">
                                <ButtonWrapper>
                                    <Button
                                        size="large"
                                        appearance="secondary"
                                        width="fill"
                                        href={linkTo.briefCreate(
                                            'square-online-custom',
                                            designerId
                                        )}
                                        type="button"
                                    >
                                        {__('Get quote')}
                                    </Button>
                                </ButtonWrapper>
                            </Box>
                        }
                    />
                </Stack>
            </Stack>
        </ContentSetting>
    );
}
